import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Mean, Median, Mode on a Dot Plot
// ─────────────────────────────────────────────────────────────────
function DiagramMeanMedianMode() {
  const W = 900, H = 320;

  // Dataset: exam scores
  const data = [52, 61, 65, 68, 70, 70, 72, 74, 75, 75, 75, 78, 80, 82, 85, 88, 91, 95];
  const n = data.length;
  const mean = data.reduce((a, b) => a + b, 0) / n;
  const sorted = [...data].sort((a, b) => a - b);
  const median = n % 2 === 0 ? (sorted[n/2-1] + sorted[n/2]) / 2 : sorted[Math.floor(n/2)];

  // Mode: 75 appears 3 times
  const freq = {};
  data.forEach(v => { freq[v] = (freq[v] || 0) + 1; });
  const maxFreq = Math.max(...Object.values(freq));
  const modes = Object.keys(freq).filter(k => freq[k] === maxFreq).map(Number);
  const mode = modes[0];

  // Layout
  const minVal = 45, maxVal = 100;
  const axY = 230, leftX = 60, rightX = 860;
  const toX = v => leftX + (v - minVal) / (maxVal - minVal) * (rightX - leftX);
  const dotR = 8, dotGap = 18;

  // Stack dots by value
  const stacks = {};
  data.forEach(v => { stacks[v] = (stacks[v] || 0) + 1; });

  // Right-side formula panel
  const panX = 630;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Mean, Median, and Mode on a Dot Plot — 18 Exam Scores</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Axis line */}
        <line x1={leftX} y1={axY} x2={rightX} y2={axY} stroke="#1F3864" strokeWidth="2"/>

        {/* Axis ticks and labels */}
        {[50,55,60,65,70,75,80,85,90,95,100].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={axY-5} x2={toX(v)} y2={axY+5} stroke="#1F3864" strokeWidth="1.5"/>
            <text x={toX(v)} y={axY+18} textAnchor="middle" fontSize="12" fill="#888">{v}</text>
          </g>
        ))}
        <text x={(leftX+rightX)/2} y={axY+34} textAnchor="middle" fontSize="12" fill="#888" fontStyle="italic">Exam Score</text>

        {/* Dots */}
        {Object.entries(stacks).map(([val, count]) => {
          const vn = Number(val);
          return Array.from({ length: count }, (_, i) => (
            <circle
              key={`${val}-${i}`}
              cx={toX(vn)}
              cy={axY - dotR - i * dotGap - 4}
              r={dotR}
              fill={vn === mode ? '#9b59b6' : '#2E75B6'}
              stroke="#fff"
              strokeWidth="1.5"
              opacity="0.88"
            />
          ));
        })}

        {/* Mode annotation — purple dots */}
        <rect x={toX(mode)-18} y={axY - dotR - (maxFreq-1)*dotGap - 30} width={36} height={16} rx={3} fill="#9b59b6"/>
        <text x={toX(mode)} y={axY - dotR - (maxFreq-1)*dotGap - 19} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">MODE</text>
        <line x1={toX(mode)} y1={axY - dotR - (maxFreq-1)*dotGap - 14} x2={toX(mode)} y2={axY - dotR - (maxFreq-1)*dotGap - 4} stroke="#9b59b6" strokeWidth="1.5"/>

        {/* Mean line */}
        <line x1={toX(mean)} y1={axY+8} x2={toX(mean)} y2={axY - 120} stroke="#c0392b" strokeWidth="2.5" strokeDasharray="7,4"/>
        <rect x={toX(mean)-26} y={axY - 135} width={52} height={18} rx={3} fill="#c0392b"/>
        <text x={toX(mean)} y={axY - 122} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">MEAN</text>
        <text x={toX(mean)} y={axY + 50} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">{mean.toFixed(1)}</text>

        {/* Median line */}
        <line x1={toX(median)} y1={axY+8} x2={toX(median)} y2={axY - 100} stroke="#27ae60" strokeWidth="2.5" strokeDasharray="7,4"/>
        <rect x={toX(median)-30} y={axY - 115} width={60} height={18} rx={3} fill="#27ae60"/>
        <text x={toX(median)} y={axY - 102} textAnchor="middle" fontSize="10" fill="#fff" fontWeight="700">MEDIAN</text>
        <text x={toX(median)} y={axY + 64} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">{median.toFixed(1)}</text>

        {/* Mode label on axis */}
        <text x={toX(mode)} y={axY + 50} textAnchor="middle" fontSize="11" fill="#9b59b6" fontWeight="700">{mode}</text>

        {/* Formula panel */}
        <rect x={panX} y={10} width={W - panX - 14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX + (W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Formulas</text>

        {[
          { y:36,  color:'#c0392b', fill:'#fff0f0', bc:'#c0392b',
            label:'Mean  x̄',
            lines:['x̄ = (Σxᵢ) / n', `= ${data.reduce((a,b)=>a+b,0)} / ${n}`, `= ${mean.toFixed(2)}`] },
          { y:116, color:'#27ae60', fill:'#f0fff4', bc:'#27ae60',
            label:'Median',
            lines:['Sort, take middle value', n%2===0?`= (${sorted[n/2-1]}+${sorted[n/2]})/2`:`= ${sorted[Math.floor(n/2)]}`, `= ${median}`] },
          { y:196, color:'#9b59b6', fill:'#f5d0ff', bc:'#9b59b6',
            label:'Mode',
            lines:['Most frequent value', `${mode} appears ${maxFreq}×`, 'Can be multiple values'] },
        ].map(({ y, color, fill, bc, label, lines }) => (
          <g key={label}>
            <rect x={panX+8} y={y} width={W-panX-30} height={74} rx={4} fill={fill} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={color} fontWeight="700">{label}</text>
            {lines.map((ln, j) => (
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="11" fill={j===0?'#1F3864':'#555'}
                fontFamily="'Courier New',monospace">{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The dot plot shows 18 exam scores stacked above a number line. The mean (red dashed) balances all values like a see-saw. The median (green dashed) splits the sorted list in half. The mode (purple dots) is the most common value — here 75, which appears three times. All three are close, suggesting a roughly symmetric distribution.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — How Distribution Shape Affects the Three Measures
// ─────────────────────────────────────────────────────────────────
function DiagramSkewness() {
  const W = 900, H = 320;

  // Three smooth histogram-like curves
  // Left skew (negative): tail on left  — mean < median < mode
  // Symmetric: mean = median = mode
  // Right skew (positive): tail on right — mode < median < mean

  const panW = (W - 24) / 3 - 5;

  // Approximate bell/skew shapes via discrete bins
  const bins = Array.from({ length: 20 }, (_, i) => i + 1);

  const symH   = bins.map(x => Math.exp(-0.5 * ((x - 10.5)/2.8)**2));
  const rightH = bins.map(x => Math.exp(-0.5 * ((x - 6)/1.8)**2) * 0.95 + Math.exp(-0.5 * ((x - 14)/4.5)**2) * 0.05);
  const leftH  = bins.map(x => Math.exp(-0.5 * ((x - 15)/1.8)**2) * 0.95 + Math.exp(-0.5 * ((x - 6)/4.5)**2) * 0.05);

  const normalize = arr => { const m = Math.max(...arr); return arr.map(v => v/m); };
  const symN = normalize(symH), rightN = normalize(rightH), leftN = normalize(leftH);

  const panels = [
    {
      title: 'Left (Negative) Skew',
      subtitle: 'Tail on the LEFT',
      heights: leftN,
      modeIdx: 14,   // ~bin 15
      medianIdx: 12, // median slightly left of mode
      meanIdx: 10,   // mean furthest left
      modeLabel: 'Mode',
      medLabel: 'Median',
      meanLabel: 'Mean',
      modeColor: '#9b59b6', medColor: '#27ae60', meanColor: '#c0392b',
      order: 'Mean < Median < Mode',
      fill: '#fff7ee', bc: '#e07b00',
      note: 'A long left tail pulls the\nmean below the median.',
    },
    {
      title: 'Symmetric (Normal)',
      subtitle: 'No skew',
      heights: symN,
      modeIdx: 9, medianIdx: 9, meanIdx: 9,
      modeLabel: 'Mode', medLabel: 'Median', meanLabel: 'Mean',
      modeColor: '#9b59b6', medColor: '#27ae60', meanColor: '#c0392b',
      order: 'Mean = Median = Mode',
      fill: '#EBF3FB', bc: '#2E75B6',
      note: 'Perfect symmetry — all three\nmeasures coincide exactly.',
    },
    {
      title: 'Right (Positive) Skew',
      subtitle: 'Tail on the RIGHT',
      heights: rightN,
      modeIdx: 5, medianIdx: 7, meanIdx: 9,
      modeLabel: 'Mode', medLabel: 'Median', meanLabel: 'Mean',
      modeColor: '#9b59b6', medColor: '#27ae60', meanColor: '#c0392b',
      order: 'Mode < Median < Mean',
      fill: '#fff0f0', bc: '#c0392b',
      note: 'A long right tail pulls the\nmean above the median.',
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>How Skewness Separates Mean, Median, and Mode</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {panels.map(({ title, subtitle, heights, modeIdx, medianIdx, meanIdx,
            modeLabel, medLabel, meanLabel, modeColor, medColor, meanColor,
            order, fill, bc, note }, pi) => {
          const px = 10 + pi * (panW + 8);
          const barW2 = (panW - 32) / heights.length;
          const barBase = 230;
          const maxBarH = 140;
          const barX = i => px + 16 + i * barW2;

          return (
            <g key={pi}>
              {/* Panel */}
              <rect x={px} y={10} width={panW} height={H-20} rx={5} fill={fill} stroke={bc} strokeWidth="1.8"/>
              <rect x={px+4} y={14} width={panW-8} height={30} rx={3} fill={bc}/>
              <text x={px+panW/2} y={30} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={42} textAnchor="middle" fontSize="10" fill="#fff">{subtitle}</text>

              {/* Bars */}
              {heights.map((h, i) => {
                const bh = h * maxBarH;
                return (
                  <rect key={i}
                    x={barX(i)+1} y={barBase - bh}
                    width={Math.max(1, barW2-2)} height={bh}
                    fill={bc} opacity={0.25 + h*0.5} rx={1}
                  />
                );
              })}

              {/* Baseline */}
              <line x1={px+14} y1={barBase} x2={px+panW-14} y2={barBase} stroke="#aaa" strokeWidth="1.2"/>

              {/* Vertical lines for mean/median/mode */}
              {[
                { idx: modeIdx, color: modeColor, label: modeLabel, dash: '' },
                { idx: medianIdx, color: medColor, label: medLabel, dash: '6,3' },
                { idx: meanIdx, color: meanColor, label: meanLabel, dash: '4,4' },
              ].map(({ idx, color, label, dash }, li) => {
                const cx = barX(idx) + barW2 / 2;
                const top = barBase - heights[idx] * maxBarH - 8;
                return (
                  <g key={li}>
                    <line x1={cx} y1={barBase+4} x2={cx} y2={top}
                      stroke={color} strokeWidth="2.2" strokeDasharray={dash}/>
                    <circle cx={cx} cy={barBase+4} r={4} fill={color} stroke="#fff" strokeWidth="1.2"/>
                    <rect x={cx - 20} y={top - 15} width={40} height={14} rx={3} fill={color}/>
                    <text x={cx} y={top - 5} textAnchor="middle" fontSize="9" fill="#fff" fontWeight="700">{label}</text>
                  </g>
                );
              })}

              {/* Order label */}
              <rect x={px+8} y={245} width={panW-16} height={20} rx={3} fill={bc} fillOpacity="0.15"/>
              <text x={px+panW/2} y={258} textAnchor="middle" fontSize="11" fill={bc} fontWeight="700"
                fontFamily="'Courier New',monospace">{order}</text>

              {/* Note */}
              {note.split('\n').map((ln, j) => (
                <text key={j} x={px+panW/2} y={278+j*16} textAnchor="middle" fontSize="11" fill="#555">{ln}</text>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Distribution shape determines the relationship between the three measures. In a symmetric distribution they coincide. A right tail (e.g., income, house prices) drags the mean rightward past the median. A left tail (e.g., retirement age) pulls the mean leftward. The median is always between the mean and mode in a skewed unimodal distribution.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — The Effect of Outliers
// ─────────────────────────────────────────────────────────────────
function DiagramOutliers() {
  const W = 900, H = 330;

  // Dataset without outlier
  const base = [42, 45, 47, 48, 50, 51, 52, 53, 55, 57];
  const withOutlier = [...base, 120];

  const mean1 = base.reduce((a,b)=>a+b,0)/base.length;
  const mean2 = withOutlier.reduce((a,b)=>a+b,0)/withOutlier.length;

  const med = arr => { const s=[...arr].sort((a,b)=>a-b); const n=s.length; return n%2===0?(s[n/2-1]+s[n/2])/2:s[Math.floor(n/2)]; };
  const med1 = med(base), med2 = med(withOutlier);

  // Two dot plots, stacked
  const minV = 35, maxV = 130;
  const axY1 = 105, axY2 = 220;
  const lx = 50, rx = 590;
  const toX = v => lx + (v - minV) / (maxV - minV) * (rx - lx);
  const dotR = 9;

  const panX = 610, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Effect of Outliers on Mean vs. Median</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── Left: two dot plots ── */}
        <rect x={10} y={10} width={590} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={305} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Adding one outlier (120) dramatically shifts the mean — but barely moves the median</text>

        {/* Dataset 1: without outlier */}
        <text x={22} y={52} fontSize="12" fill="#1F3864" fontWeight="700">Dataset A — No Outlier:  {base.join(', ')}</text>

        {/* Axis 1 */}
        <line x1={lx} y1={axY1} x2={rx+10} y2={axY1} stroke="#bbb" strokeWidth="1.5"/>
        {[40,50,60,70,80,90,100,110,120].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={axY1-3} x2={toX(v)} y2={axY1+3} stroke="#bbb" strokeWidth="1"/>
            <text x={toX(v)} y={axY1+14} textAnchor="middle" fontSize="10" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Dots 1 */}
        {base.map((v, i) => (
          <circle key={i} cx={toX(v)} cy={axY1-dotR-4} r={dotR} fill="#2E75B6" stroke="#fff" strokeWidth="1.5" opacity="0.85"/>
        ))}

        {/* Mean and Median lines — dataset 1 */}
        <line x1={toX(mean1)} y1={axY1+6} x2={toX(mean1)} y2={axY1-38} stroke="#c0392b" strokeWidth="2.5"/>
        <text x={toX(mean1)} y={axY1-42} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">x̄ = {mean1.toFixed(1)}</text>

        <line x1={toX(med1)} y1={axY1+6} x2={toX(med1)} y2={axY1-55} stroke="#27ae60" strokeWidth="2.5" strokeDasharray="6,3"/>
        <text x={toX(med1)} y={axY1-58} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">Med = {med1.toFixed(1)}</text>

        {/* Dataset 2: with outlier */}
        <text x={22} y={152} fontSize="12" fill="#1F3864" fontWeight="700">Dataset B — With Outlier:  {base.join(', ')}, <tspan fill="#c0392b" fontWeight="700">120</tspan></text>

        {/* Axis 2 */}
        <line x1={lx} y1={axY2} x2={rx+10} y2={axY2} stroke="#bbb" strokeWidth="1.5"/>
        {[40,50,60,70,80,90,100,110,120].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={axY2-3} x2={toX(v)} y2={axY2+3} stroke="#bbb" strokeWidth="1"/>
            <text x={toX(v)} y={axY2+14} textAnchor="middle" fontSize="10" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Dots 2 */}
        {base.map((v, i) => (
          <circle key={i} cx={toX(v)} cy={axY2-dotR-4} r={dotR} fill="#2E75B6" stroke="#fff" strokeWidth="1.5" opacity="0.85"/>
        ))}
        {/* Outlier dot */}
        <circle cx={toX(120)} cy={axY2-dotR-4} r={dotR+2} fill="#c0392b" stroke="#fff" strokeWidth="2"/>
        <text x={toX(120)} y={axY2-dotR-18} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">OUTLIER</text>

        {/* Mean and Median lines — dataset 2 */}
        <line x1={toX(mean2)} y1={axY2+6} x2={toX(mean2)} y2={axY2-38} stroke="#c0392b" strokeWidth="2.5"/>
        <text x={toX(mean2)} y={axY2-42} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">x̄ = {mean2.toFixed(1)}</text>

        <line x1={toX(med2)} y1={axY2+6} x2={toX(med2)} y2={axY2-55} stroke="#27ae60" strokeWidth="2.5" strokeDasharray="6,3"/>
        <text x={toX(med2)} y={axY2-58} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">Med = {med2.toFixed(1)}</text>

        {/* Shift arrow for mean */}
        <defs>
          <marker id="arrowR" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
            <polygon points="0 0,8 4,0 8" fill="#c0392b"/>
          </marker>
        </defs>
        <line x1={toX(mean1)+4} y1={axY1+50} x2={toX(mean2)-4} y2={axY1+50}
          stroke="#c0392b" strokeWidth="2" markerEnd="url(#arrowR)" strokeDasharray="4,2"/>
        <text x={(toX(mean1)+toX(mean2))/2} y={axY1+64} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">
          Mean shifts +{(mean2-mean1).toFixed(1)} pts
        </text>
        <text x={(toX(med1)+toX(med2))/2+40} y={axY1+64} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">
          Median shifts only +{(med2-med1).toFixed(1)} pt
        </text>

        {/* ── Right panel ── */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Key Comparison</text>

        {[
          { y:36,  bg:'#fff0f0', bc:'#c0392b',
            lines:['Mean  (sensitive)', 'Uses all values equally.', 'One extreme value pulls it', 'strongly toward the tail.', 'x̄ shifted by +' + (mean2-mean1).toFixed(1) + ' points.'] },
          { y:148, bg:'#f0fff4', bc:'#27ae60',
            lines:['Median  (robust)', 'Uses only the middle', 'position in sorted data.', 'Outlier barely changes it:', 'Shifted only +' + (med2-med1).toFixed(1) + ' point.'] },
          { y:260, bg:'#EBF3FB', bc:'#2E75B6',
            lines:['Rule of thumb', 'Skewed data or outliers?', '→ prefer the median.', 'Symmetric, no outliers?', '→ mean is fine.'] },
        ].map(({ y, bg, bc, lines }) => (
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={104} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            {lines.map((ln, j) => (
              <text key={j} x={panX+16} y={y+18+j*17} fontSize="11"
                fill={j===0 ? bc : '#444'}
                fontWeight={j===0 ? '700' : '400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Dataset A has a mean of {mean1.toFixed(1)} and median of {med1.toFixed(1)} — nearly identical. Adding a single outlier (120) shifts the mean to {mean2.toFixed(1)}, a jump of {(mean2-mean1).toFixed(1)} points, while the median only moves to {med2.toFixed(1)}. This is why income and house-price statistics typically report the median: a handful of extremely high values would make the mean unrepresentative of the typical person.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Weighted Mean and Grouped Data
// ─────────────────────────────────────────────────────────────────
function DiagramWeightedMean() {
  const W = 900, H = 340;

  // Example 1: GPA weighted by credit hours
  const courses = [
    { name: 'Calculus',   grade: 4.0, credits: 4, color: '#2E75B6' },
    { name: 'English',    grade: 3.7, credits: 3, color: '#27ae60' },
    { name: 'Physics',    grade: 3.3, credits: 4, color: '#c0392b' },
    { name: 'History',    grade: 3.0, credits: 2, color: '#9b59b6' },
    { name: 'Lab',        grade: 2.7, credits: 1, color: '#e07b00' },
  ];
  const totalCredits = courses.reduce((a, c) => a + c.credits, 0);
  const weightedSum  = courses.reduce((a, c) => a + c.grade * c.credits, 0);
  const wMean = weightedSum / totalCredits;
  const simpMean = courses.reduce((a,c)=>a+c.grade,0)/courses.length;

  // Example 2: grouped data frequency table
  const groups = [
    { label:'60–69', mid:64.5, freq:3 },
    { label:'70–79', mid:74.5, freq:8 },
    { label:'80–89', mid:84.5, freq:11},
    { label:'90–99', mid:94.5, freq:6 },
  ];
  const totalN = groups.reduce((a,g)=>a+g.freq,0);
  const groupedMean = groups.reduce((a,g)=>a+g.mid*g.freq,0)/totalN;

  // Bar chart for credits
  const barOx = 55, barBase = 220, barScY = 45, barW = 65, barGap = 14;

  // Right panel for grouped data
  const panX = 490;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Weighted Mean — GPA by Credit Hours  |  Estimated Mean from Grouped Data</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: Weighted GPA ── */}
        <rect x={10} y={10} width={470} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={245} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Weighted GPA Calculation  (credits = weights)</text>

        {/* Bars representing credit hours */}
        {courses.map((c, i) => {
          const bx = barOx + i * (barW + barGap);
          const bh = c.credits * barScY;
          return (
            <g key={i}>
              <rect x={bx} y={barBase - bh} width={barW} height={bh} fill={c.color} opacity="0.82" rx={3}/>
              {/* Grade label inside bar */}
              <text x={bx+barW/2} y={barBase-bh+16} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
                {c.grade.toFixed(1)}
              </text>
              {/* Credits label */}
              <rect x={bx+4} y={barBase-bh+20} width={barW-8} height={16} rx={2} fill="#fff" fillOpacity="0.25"/>
              <text x={bx+barW/2} y={barBase-bh+31} textAnchor="middle" fontSize="10" fill="#fff">
                {c.credits} cr
              </text>
              {/* Course name */}
              <text x={bx+barW/2} y={barBase+14} textAnchor="middle" fontSize="10" fill="#555">{c.name}</text>
              {/* Contribution = grade × credits */}
              <text x={bx+barW/2} y={barBase+28} textAnchor="middle" fontSize="9" fill={c.color} fontWeight="700">
                {(c.grade*c.credits).toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Axis */}
        <line x1={barOx-5} y1={barBase} x2={barOx + courses.length*(barW+barGap)} y2={barBase} stroke="#bbb" strokeWidth="1.5"/>

        {/* y-axis credit ticks */}
        {[1,2,3,4].map(v => (
          <g key={v}>
            <line x1={barOx-8} y1={barBase-v*barScY} x2={barOx-2} y2={barBase-v*barScY} stroke="#bbb" strokeWidth="1"/>
            <text x={barOx-12} y={barBase-v*barScY+4} textAnchor="end" fontSize="10" fill="#bbb">{v}</text>
          </g>
        ))}
        <text x={barOx-24} y={barBase-2*barScY} textAnchor="middle" fontSize="9" fill="#888"
          transform={`rotate(-90,${barOx-24},${barBase-2*barScY})`}>Credits</text>
        <text x={240} y={barBase+42} textAnchor="middle" fontSize="10" fill="#888">Bar height = credit hours  |  Number inside = grade × credits</text>

        {/* Result box */}
        <rect x={18} y={H-76} width={450} height={60} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.3"/>
        <text x={243} y={H-60} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          Weighted Mean = Σ(grade × credits) / Σcredits
        </text>
        <text x={243} y={H-44} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          = {weightedSum.toFixed(1)} / {totalCredits} = {wMean.toFixed(3)}
        </text>
        <text x={243} y={H-28} textAnchor="middle" fontSize="11" fill="#c0392b">
          Simple mean (unweighted) = {simpMean.toFixed(3)}  — different because credits vary
        </text>

        {/* ── RIGHT: Grouped data mean ── */}
        <rect x={panX} y={10} width={W-panX-14} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+(W-panX-14)/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Estimated Mean from Grouped Frequency Table</text>

        {/* Table header */}
        {['Class', 'Freq (f)', 'Midpoint (m)', 'f × m'].map((h, i) => (
          <g key={i}>
            <rect x={panX+8+i*90} y={36} width={88} height={22} rx={2} fill="#1F3864"/>
            <text x={panX+52+i*90} y={51} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{h}</text>
          </g>
        ))}

        {/* Table rows */}
        {groups.map((g, i) => {
          const fm = g.mid * g.freq;
          return (
            <g key={i}>
              <rect x={panX+8} y={60+i*34} width={4} height={32} fill={['#2E75B6','#c0392b','#27ae60','#9b59b6'][i]}/>
              {[g.label, g.freq, g.mid, fm.toFixed(1)].map((val, j) => (
                <g key={j}>
                  <rect x={panX+12+j*90} y={60+i*34} width={84} height={32} rx={1}
                    fill={i%2===0?'#f8fbff':'#fff'} stroke="#eee" strokeWidth="0.5"/>
                  <text x={panX+54+j*90} y={60+i*34+20} textAnchor="middle" fontSize="12"
                    fill={j===0 ? ['#2E75B6','#c0392b','#27ae60','#9b59b6'][i] : '#333'}
                    fontFamily="'Courier New',monospace" fontWeight={j===0?'700':'400'}>{val}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Totals row */}
        <rect x={panX+8} y={60+groups.length*34} width={360} height={28} rx={2} fill="#1F3864"/>
        <text x={panX+60}  y={60+groups.length*34+18} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">Total</text>
        <text x={panX+150} y={60+groups.length*34+18} textAnchor="middle" fontSize="11" fill="#fff" fontFamily="'Courier New',monospace" fontWeight="700">{totalN}</text>
        <text x={panX+330} y={60+groups.length*34+18} textAnchor="middle" fontSize="11" fill="#fff" fontFamily="'Courier New',monospace" fontWeight="700">{groups.reduce((a,g)=>a+g.mid*g.freq,0).toFixed(1)}</text>

        {/* Formula and result */}
        <rect x={panX+8} y={60+groups.length*34+36} width={W-panX-30} height={56} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.2"/>
        <text x={panX+(W-panX-14)/2} y={60+groups.length*34+52} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          x̄ ≈ Σ(f × m) / Σf
        </text>
        <text x={panX+(W-panX-14)/2} y={60+groups.length*34+68} textAnchor="middle" fontSize="12" fill="#27ae60"
          fontFamily="'Courier New',monospace" fontWeight="700">
          = {groups.reduce((a,g)=>a+g.mid*g.freq,0).toFixed(1)} / {totalN} ≈ {groupedMean.toFixed(2)}
        </text>
        <text x={panX+(W-panX-14)/2} y={60+groups.length*34+82} textAnchor="middle" fontSize="10" fill="#555">
          Uses class midpoints as representatives of each group
        </text>
      </svg>
      <div style={S.dCaption}>
        A weighted mean assigns different importance to each value. GPA uses credit hours as weights — a 4.0 in a 4-credit course contributes more than a 4.0 in a 1-credit course. For grouped/frequency data where individual values are unavailable, the class midpoint estimates each value and the formula x̄ ≈ Σ(f×m)/Σf gives the estimated mean.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Five-Number Summary and Box Plot
// ─────────────────────────────────────────────────────────────────
function DiagramBoxPlot() {
  const W = 900, H = 320;

  // Two datasets for side-by-side box plots
  const datasets = [
    {
      label: 'Class A', color: '#2E75B6', fill: '#d0e8ff',
      data: [42, 55, 58, 60, 62, 64, 65, 67, 68, 70, 71, 72, 73, 75, 78, 80, 85, 92],
    },
    {
      label: 'Class B', color: '#c0392b', fill: '#fde8e8',
      data: [30, 45, 55, 60, 61, 62, 63, 65, 66, 67, 68, 70, 72, 75, 80, 88, 95, 98],
    },
  ];

  const stats = datasets.map(({ data, label, color, fill }) => {
    const s = [...data].sort((a,b)=>a-b);
    const n = s.length;
    const q1 = s[Math.floor(n/4)];
    const q2 = n%2===0?(s[n/2-1]+s[n/2])/2:s[Math.floor(n/2)];
    const q3 = s[Math.floor(3*n/4)];
    const iqr = q3 - q1;
    const loFence = q1 - 1.5*iqr;
    const hiFence = q3 + 1.5*iqr;
    const whiskerLo = s.find(v => v >= loFence);
    const whiskerHi = [...s].reverse().find(v => v <= hiFence);
    const outliers = s.filter(v => v < loFence || v > hiFence);
    const mean = s.reduce((a,b)=>a+b,0)/n;
    return { label, color, fill, s, q1, q2, q3, iqr, whiskerLo, whiskerHi, outliers, mean, min:s[0], max:s[n-1] };
  });

  const minV = 25, maxV = 105;
  const lx = 60, rx = 540;
  const toX = v => lx + (v - minV) / (maxV - minV) * (rx - lx);
  const bpYs = [105, 185]; // y center for each box plot
  const bpH = 40;

  const panX = 560, panW = W - panX - 14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Five-Number Summary and Box-and-Whisker Plots — Two Classes Compared</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        <rect x={10} y={10} width={540} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={285} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Box plots encode Min, Q1, Median, Q3, Max — and flag outliers</text>

        {/* Shared axis */}
        <line x1={lx} y1={240} x2={rx+10} y2={240} stroke="#bbb" strokeWidth="1.5"/>
        {[30,40,50,60,70,80,90,100].map(v => (
          <g key={v}>
            <line x1={toX(v)} y1={237} x2={toX(v)} y2={243} stroke="#bbb" strokeWidth="1"/>
            <text x={toX(v)} y={254} textAnchor="middle" fontSize="11" fill="#aaa">{v}</text>
          </g>
        ))}

        {stats.map((st, i) => {
          const cy = bpYs[i];
          const top = cy - bpH/2, bot = cy + bpH/2;
          return (
            <g key={i}>
              {/* Dataset label */}
              <text x={22} y={cy+4} fontSize="12" fill={st.color} fontWeight="700">{st.label}</text>

              {/* Whisker lines */}
              <line x1={toX(st.whiskerLo)} y1={cy} x2={toX(st.q1)} y2={cy} stroke={st.color} strokeWidth="1.8"/>
              <line x1={toX(st.q3)} y1={cy} x2={toX(st.whiskerHi)} y2={cy} stroke={st.color} strokeWidth="1.8"/>
              {/* Whisker end caps */}
              <line x1={toX(st.whiskerLo)} y1={top+8} x2={toX(st.whiskerLo)} y2={bot-8} stroke={st.color} strokeWidth="1.8"/>
              <line x1={toX(st.whiskerHi)} y1={top+8} x2={toX(st.whiskerHi)} y2={bot-8} stroke={st.color} strokeWidth="1.8"/>

              {/* IQR box */}
              <rect x={toX(st.q1)} y={top} width={toX(st.q3)-toX(st.q1)} height={bpH}
                fill={st.fill} stroke={st.color} strokeWidth="2"/>

              {/* Median line */}
              <line x1={toX(st.q2)} y1={top} x2={toX(st.q2)} y2={bot} stroke={st.color} strokeWidth="3"/>

              {/* Mean diamond */}
              {(() => {
                const mx = toX(st.mean), d = 7;
                return <polygon points={`${mx},${cy-d} ${mx+d},${cy} ${mx},${cy+d} ${mx-d},${cy}`}
                  fill={st.color} stroke="#fff" strokeWidth="1.5" opacity="0.7"/>;
              })()}

              {/* Outlier dots */}
              {st.outliers.map((v, j) => (
                <circle key={j} cx={toX(v)} cy={cy} r={5} fill="none" stroke={st.color} strokeWidth="2"/>
              ))}

              {/* Labels above box */}
              {[
                { v: st.whiskerLo, label: 'Min*' },
                { v: st.q1,        label: 'Q1' },
                { v: st.q2,        label: 'Med' },
                { v: st.q3,        label: 'Q3' },
                { v: st.whiskerHi, label: 'Max*' },
              ].map(({ v, label }) => (
                <g key={label}>
                  <text x={toX(v)} y={top-5} textAnchor="middle" fontSize="9" fill={st.color} fontWeight="700">{label}</text>
                  <text x={toX(v)} y={top-14} textAnchor="middle" fontSize="9" fill="#888">{v}</text>
                </g>
              ))}
            </g>
          );
        })}

        {/* Legend */}
        <rect x={lx+10} y={262} width={300} height={22} rx={3} fill="#f8fbff" stroke="#eee"/>
        <line x1={lx+20} y1={273} x2={lx+44} y2={273} stroke="#555" strokeWidth="2"/>
        <polygon points={`${lx+32},${273-5} ${lx+37},${273} ${lx+32},${273+5} ${lx+27},${273}`} fill="#555"/>
        <text x={lx+50} y={277} fontSize="10" fill="#555">◆ = mean   □ = outlier   whiskers extend to 1.5×IQR</text>

        {/* ── Right panel: five-number summary table ── */}
        <rect x={panX} y={10} width={panW} height={H-20} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={28} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">Five-Number Summary</text>

        {/* Header */}
        {['Statistic','Class A','Class B'].map((h, i) => (
          <rect key={i} x={panX+8+i*100} y={36} width={98} height={22} rx={2} fill={i===0?'#1F3864':i===1?'#2E75B6':'#c0392b'}/>
        ))}
        {['Statistic','Class A','Class B'].map((h, i) => (
          <text key={i} x={panX+57+i*100} y={51} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{h}</text>
        ))}

        {[
          ['Minimum',  stats[0].min,          stats[1].min],
          ['Q1 (25%)', stats[0].q1,           stats[1].q1],
          ['Median',   stats[0].q2,           stats[1].q2],
          ['Mean ◆',   stats[0].mean.toFixed(1), stats[1].mean.toFixed(1)],
          ['Q3 (75%)', stats[0].q3,           stats[1].q3],
          ['Maximum',  stats[0].max,          stats[1].max],
          ['IQR',      stats[0].iqr,          stats[1].iqr],
        ].map(([label, a, b], i) => (
          <g key={label}>
            <rect x={panX+8}   y={60+i*30} width={98} height={28} fill={i%2===0?'#f8fbff':'#fff'} stroke="#eee" strokeWidth="0.5"/>
            <rect x={panX+108} y={60+i*30} width={98} height={28} fill={i%2===0?'#f0f7ff':'#fff'} stroke="#eee" strokeWidth="0.5"/>
            <rect x={panX+208} y={60+i*30} width={98} height={28} fill={i%2===0?'#fff5f5':'#fff'} stroke="#eee" strokeWidth="0.5"/>
            <text x={panX+16}  y={60+i*30+18} fontSize="11" fill="#1F3864" fontWeight="700">{label}</text>
            <text x={panX+157} y={60+i*30+18} textAnchor="middle" fontSize="12" fill="#2E75B6"
              fontFamily="'Courier New',monospace" fontWeight="700">{a}</text>
            <text x={panX+257} y={60+i*30+18} textAnchor="middle" fontSize="12" fill="#c0392b"
              fontFamily="'Courier New',monospace" fontWeight="700">{b}</text>
          </g>
        ))}

        {/* Note on IQR */}
        <rect x={panX+8} y={H-52} width={panW-16} height={36} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={panX+panW/2} y={H-37} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">IQR = Q3 − Q1 (middle 50% of data)</text>
        <text x={panX+panW/2} y={H-22} textAnchor="middle" fontSize="10" fill="#555">Outlier threshold: below Q1−1.5×IQR or above Q3+1.5×IQR</text>
      </svg>
      <div style={S.dCaption}>
        A box plot shows the full five-number summary in one compact graphic. The box spans the interquartile range (Q1 to Q3) — the middle 50% of data. The median line inside the box is the most visible feature. Whiskers extend to the furthest non-outlier values (within 1.5×IQR of the box). Points beyond the whiskers are plotted individually as suspected outliers. The diamond marks the mean.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Choosing the Right Measure: Decision Guide
// ─────────────────────────────────────────────────────────────────
function DiagramChoosingMeasure() {
  const W = 900, H = 340;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Choosing the Right Measure of Central Tendency — A Practical Guide</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* Header */}
        <rect x={14} y={10} width={W-28} height={38} rx={5} fill="#1F3864"/>
        <text x={W/2} y={26} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">No single measure is always best — context determines the right choice</text>
        <text x={W/2} y={42} textAnchor="middle" fontSize="11" fill="#aac8e8">Use the data type, distribution shape, and purpose of the analysis to guide your decision</text>

        {/* Three big cards */}
        {[
          {
            x:14, w:270, color:'#c0392b', fill:'#fff0f0',
            title:'Mean  (x̄)',
            subtitle:'Best for symmetric, interval/ratio data',
            pros:['Uses every data point equally','Mathematically tractable (algebra, calculus)','Minimises sum of squared deviations','Essential for further statistics (SD, t-tests, ANOVA)'],
            cons:['Sensitive to outliers and extreme skew','Can be misleading if distribution is bimodal','Inappropriate for ordinal/categorical data'],
            examples:['Heights, weights, temperatures','Test scores (no extreme outliers)','Physics measurements'],
          },
          {
            x:294, w:270, color:'#27ae60', fill:'#f0fff4',
            title:'Median',
            subtitle:'Best for skewed or ordinal data',
            pros:['Resistant to outliers','Better "typical value" for skewed distributions','Works for ordinal data (rankings)','Unaffected by extreme values on one side'],
            cons:['Ignores actual magnitude of values','Less useful for further mathematical analysis','Harder to work with algebraically'],
            examples:['Income, house prices, rent','Response time, waiting time','Satisfaction ratings (1–5 scale)'],
          },
          {
            x:574, w:298, color:'#9b59b6', fill:'#f5d0ff',
            title:'Mode',
            subtitle:'Best for categorical data or finding peaks',
            pros:['Only measure valid for nominal data','Identifies most popular/common category','Useful for bimodal or multimodal distributions','Easy to compute and communicate'],
            cons:['May not exist, or not be unique','Ignores spread and all other values','Unstable — small changes can shift it drastically'],
            examples:['Favourite colour, shoe size','Most common diagnosis in a clinic','Peak hour of customer arrivals'],
          },
        ].map(({ x, w, color, fill, title, subtitle, pros, cons, examples }) => (
          <g key={title}>
            <rect x={x} y={58} width={w} height={H-68} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
            <rect x={x+4} y={62} width={w-8} height={30} rx={3} fill={color}/>
            <text x={x+w/2} y={78} textAnchor="middle" fontSize="14" fill="#fff" fontWeight="700">{title}</text>
            <text x={x+w/2} y={90} textAnchor="middle" fontSize="10" fill="#fff">{subtitle}</text>

            {/* Pros */}
            <text x={x+12} y={110} fontSize="11" fill="#1F3864" fontWeight="700">✓ Use when:</text>
            {pros.map((p, j) => (
              <g key={j}>
                <circle cx={x+18} cy={123+j*16-3} r={3} fill={color}/>
                <text x={x+26} y={123+j*16} fontSize="10" fill="#333">{p}</text>
              </g>
            ))}

            {/* Cons */}
            <text x={x+12} y={194} fontSize="11" fill="#c0392b" fontWeight="700">✗ Limitations:</text>
            {cons.map((c, j) => (
              <g key={j}>
                <text x={x+18} y={208+j*15} fontSize="10" fill="#888">— {c}</text>
              </g>
            ))}

            {/* Examples */}
            <line x1={x+10} y1={262} x2={x+w-10} y2={262} stroke="#ddd" strokeWidth="1"/>
            <text x={x+12} y={276} fontSize="11" fill={color} fontWeight="700">Real-world examples:</text>
            {examples.map((e, j) => (
              <text key={j} x={x+18} y={290+j*14} fontSize="10" fill="#555" fontStyle="italic">{e}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        The mean is the workhorse of statistics — it feeds into variance, standard deviation, confidence intervals, and hypothesis tests. The median is the honest reporter when data is skewed or contains outliers. The mode is the only option for categorical data and the go-to for identifying popularity or clustering. In practice, reporting all three — and noting how they compare — gives a richer picture than any single value alone.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function CentralTendency(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Measures of Central Tendency</h1>

      <p style={S.p}>
        A measure of central tendency is a single number that summarises where the "centre" of a dataset lies. Rather than listing every observation, we want one representative value that captures what is typical. The three classical measures — mean, median, and mode — each define "centre" differently, and each has contexts where it shines and contexts where it misleads. Understanding when to use which measure is one of the most practically important skills in statistics.
      </p>

      <InfoBox
        title="The Three Core Measures at a Glance"
        items={[
          "Mean (x̄): arithmetic average — sum of all values divided by the count. Balances the dataset like a see-saw.",
          "Median: the middle value of the sorted dataset. Splits the distribution exactly in half by count.",
          "Mode: the most frequently occurring value. The only measure valid for categorical (nominal) data.",
          "All three coincide in a perfectly symmetric unimodal distribution (e.g. the normal distribution).",
          "Skewed distributions and outliers pull the mean away from the median — a key diagnostic signal.",
        ]}
      />

      <h2 style={S.h2}>Mean, Median, and Mode on a Dot Plot</h2>
      <DiagramMeanMedianMode />
      <Table2 rows={[
        ["Arithmetic mean",   "x̄ = (x₁+x₂+…+xₙ) / n = (Σxᵢ) / n\nSum all values, divide by the count n"],
        ["Population mean",   "μ = (Σxᵢ) / N  — Greek letter μ (mu) for the whole population\nx̄ is the sample estimate of μ"],
        ["Median (odd n)",    "Sort data; take the middle value at position (n+1)/2\nExample: {3,5,7,9,11} → median = 7"],
        ["Median (even n)",   "Sort data; average the two middle values at n/2 and n/2+1\nExample: {3,5,7,9} → median = (5+7)/2 = 6"],
        ["Mode",              "The value(s) with the highest frequency\nA dataset can be unimodal, bimodal, multimodal, or have no mode"],
        ["Midrange",          "(min + max) / 2 — crude measure, heavily affected by extremes\nRarely used in formal statistics"],
      ]} />

      <h2 style={S.h2}>How Distribution Shape Affects the Three Measures</h2>
      <DiagramSkewness />
      <Table2 rows={[
        ["Symmetric",        "Mean = Median = Mode (for unimodal symmetric distributions)\nThe normal (bell curve) distribution is the classic example"],
        ["Right (positive) skew","Mode < Median < Mean — tail on the right pulls mean upward\nExamples: income, house prices, reaction time, survival times"],
        ["Left (negative) skew", "Mean < Median < Mode — tail on the left pulls mean downward\nExamples: age at retirement, scores on an easy test"],
        ["Skewness coefficient","Pearson's: (mean − mode) / SD  or  3(mean − median) / SD\nPositive = right-skewed; negative = left-skewed; 0 = symmetric"],
        ["Bimodal",          "Two peaks — mode splits into two values, mean and median may be misleading\nReport both modes and consider whether two subgroups exist"],
        ["Rule of thumb",    "If mean ≠ median, suspect skewness or outliers\nThe gap |mean − median| relative to SD signals the degree of asymmetry"],
      ]} />

      <h2 style={S.h2}>The Effect of Outliers</h2>
      <DiagramOutliers />
      <Table2 rows={[
        ["Outlier definition", "An observation unusually far from the rest of the data\nFormal: below Q1−1.5×IQR or above Q3+1.5×IQR (Tukey's rule)"],
        ["Mean sensitivity",  "Each outlier shifts the mean in proportion to (outlier − mean) / n\nOne extreme value can dominate the mean in small samples"],
        ["Median robustness", "Median changes only when the outlier crosses the middle position\nFor large n, a single outlier barely moves the median"],
        ["Mode robustness",   "Mode is completely unaffected unless the outlier itself repeats most often\nMost robust measure for frequency-based summaries"],
        ["Trimmed mean",      "Discard the top and bottom p% of values, then take the mean\n10% trimmed mean is common — balances robustness and efficiency"],
        ["Winsorized mean",   "Replace extremes with the nearest non-outlier value (not discard)\nLess extreme than trimming; used in robust statistics"],
      ]} />

      <h2 style={S.h2}>Weighted Mean and Grouped Data</h2>
      <DiagramWeightedMean />
      <Table2 rows={[
        ["Weighted mean",     "x̄_w = Σ(wᵢ·xᵢ) / Σwᵢ  — weights reflect relative importance\nGPA, index numbers, portfolio returns all use weighted means"],
        ["Equal weights",     "When all wᵢ = 1, the weighted mean reduces to the arithmetic mean\nWeighted mean generalises arithmetic mean"],
        ["Grouped data mean", "x̄ ≈ Σ(fᵢ·mᵢ) / Σfᵢ  where mᵢ = class midpoint, fᵢ = frequency\nApproximation — exact values are unknown within each class"],
        ["Grouped data median","Median class = class containing the (n/2)th observation\nLinear interpolation: Med = L + [(n/2 − F) / f] × h"],
        ["Geometric mean",    "G = (x₁·x₂·…·xₙ)^(1/n) = exp[(1/n)Σln xᵢ]\nUsed for growth rates, ratios, and multiplicative processes"],
        ["Harmonic mean",     "H = n / Σ(1/xᵢ)  — appropriate for rates and speeds\nExample: average speed when distance is constant"],
      ]} />

      <h2 style={S.h2}>Five-Number Summary and Box Plots</h2>
      <DiagramBoxPlot />
      <Table2 rows={[
        ["Five-number summary","Min, Q1, Median, Q3, Max — a complete picture of spread and centre\nMore informative than the mean alone for skewed data"],
        ["Quartiles",         "Q1 = 25th percentile, Q2 = median = 50th, Q3 = 75th\nSplit the sorted data into four equal-count quarters"],
        ["IQR",               "Interquartile Range = Q3 − Q1 — the span of the middle 50%\nRobust measure of spread; not affected by outliers"],
        ["Outlier detection", "Mild outlier: beyond Q1−1.5×IQR or Q3+1.5×IQR\nExtreme outlier: beyond Q1−3×IQR or Q3+3×IQR"],
        ["Box plot whiskers", "Extend to the furthest observed value within 1.5×IQR of the box\nValues beyond the whiskers are plotted as individual points"],
        ["Percentiles",       "The pth percentile Xₚ: p% of values fall at or below Xₚ\nMedian = 50th percentile; quartiles = 25th and 75th"],
      ]} />

      <h2 style={S.h2}>Choosing the Right Measure</h2>
      <DiagramChoosingMeasure />
      <Table2 rows={[
        ["Data type matters", "Nominal: only mode is valid (e.g. favourite colour)\nOrdinal: median or mode (e.g. satisfaction ratings 1–5)\nInterval/Ratio: any measure, but context determines best choice"],
        ["Symmetric data",   "All three are approximately equal — use mean for efficiency\nMean minimises the sum of squared deviations from it"],
        ["Skewed data",      "Median is more representative of the 'typical' value\nExample: median income is more informative than mean income"],
        ["Categorical data", "Only the mode applies — counts and percentages\nBar charts and frequency tables are the natural companions"],
        ["Reporting practice","Best practice: report mean ± SD for symmetric data\nFor skewed data: report median and IQR (or min/max)"],
        ["Multiple modes",   "If distribution is bimodal, report both modes; a single mean/median\nmay fall between the peaks and describe neither group well"],
      ]} />

      <InfoBox
        title="Key Takeaway — Which Measure to Choose"
        items={[
          "Symmetric distribution, no outliers? → Mean is efficient and feeds into further statistics.",
          "Skewed distribution (income, prices, reaction time)? → Median is more representative.",
          "Categorical / nominal data (colours, types, names)? → Mode is the only valid option.",
          "Ordinal data (rankings, Likert scales)? → Median; mode is acceptable; mean is questionable.",
          "Outliers present? → Use median (robust) or trimmed mean; report outliers explicitly.",
          "Comparing two groups? → Box plots show five-number summaries side-by-side for quick visual comparison.",
          "Weighted importance (GPA, portfolio, index)? → Weighted mean where weights reflect importance.",
          "Growth rates, ratios, returns? → Geometric mean handles multiplicative processes correctly.",
        ]}
      />
    </div>
  );
}